import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import Container from './Container';
import Button from './Button';
import logo from '../assets/logo.jpg';

interface HeaderProps {
  isSticky?: boolean;
}

const HeaderWrapper = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
  background-color: ${props => props.theme.colors.light};
  box-shadow: ${props => props.theme.shadows.md};
  height: 80px;
`;

const HeaderContainer = styled(Container)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
  position: relative;
  z-index: 1001;
`;

const Logo = styled.img`
  max-height: ${props => props.theme.spacing.xxxl};
  width: auto;
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    max-height: ${props => props.theme.spacing.xxl};
  }
`;

const Nav = styled.nav`
  display: flex;
  align-items: center;
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    display: none;
  }
`;

const NavLink = styled(Link) <{ isActive?: boolean }>`
  color: ${props => props.theme.colors.dark};
  font-family: ${props => props.theme.fonts.body};
  font-weight: ${props => props.theme.fontWeights.semiBold};
  font-size: ${props => props.theme.fontSizes.md};
  padding: ${props => `${props.theme.spacing.sm} ${props.theme.spacing.md}`};
  margin: 0 ${props => props.theme.spacing.xs};
  text-decoration: none;
  transition: color 0.3s ease;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -4px;
    left: 0;
    width: 100%;
    height: 2px;
    background-color: ${props => props.theme.colors.primary};
    transform: scaleX(${props => (props.isActive ? 1 : 0)});
    transform-origin: left;
    transition: transform 0.3s ease;
  }
  
  &:hover {
    color: ${props => props.theme.colors.primary};
    
    &:after {
      transform: scaleX(1);
    }
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  font-size: ${props => props.theme.fontSizes.xl};
  color: ${props => props.theme.colors.dark};
  cursor: pointer;
  z-index: 1001;
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    display: block;
  }
`;

const MobileMenu = styled.div<{ isOpen: boolean }>`
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background-color: ${props => props.theme.colors.light};
  z-index: 1002;
  transition: transform 0.3s ease;
  padding: ${props => props.theme.spacing.xl};
  flex-direction: column;
  transform: translateY(${props => (props.isOpen ? '0' : '-100%')});
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    display: flex;
  }
`;

const MobileMenuCloseButton = styled.button`
  position: absolute;
  top: ${props => props.theme.spacing.md};
  right: ${props => props.theme.spacing.md};
  background: none;
  border: none;
  font-size: ${props => props.theme.fontSizes.xl};
  color: ${props => props.theme.colors.dark};
  cursor: pointer;
  z-index: 1003;
`;

const MobileNavLink = styled(Link) <{ isActive?: boolean }>`
  color: ${props => props.theme.colors.dark};
  font-family: ${props => props.theme.fonts.body};
  font-weight: ${props => props.theme.fontWeights.semiBold};
  font-size: ${props => props.theme.fontSizes.lg};
  padding: ${props => props.theme.spacing.md};
  margin: ${props => props.theme.spacing.xs} 0;
  text-decoration: none;
  border-bottom: 1px solid ${props => props.theme.colors.lightGray};
  width: 100%;
  text-align: center;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 2px;
    background-color: ${props => props.theme.colors.primary};
    transform: scaleX(${props => (props.isActive ? 1 : 0)});
    transform-origin: left;
    transition: transform 0.3s ease;
  }
  
  &:hover {
    color: ${props => props.theme.colors.primary};
  }
`;

const MobileDonateButtonWrapper = styled.div`
  margin-top: ${props => props.theme.spacing.md};
  width: 100%;
`;

const Header: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  // Check if current path matches the link path
  const isActive = (path: string) => {
    return location.pathname === path;
  };

  return (
    <HeaderWrapper>
      <HeaderContainer>
        <Link to="/">
          <Logo src={logo} alt="Project Assist Logo" />
        </Link>

        <Nav>
          <NavLink to="/" isActive={isActive('/')}>Home</NavLink>
          <NavLink to="/our-projects" isActive={isActive('/our-projects')}>Our Projects</NavLink>
          <NavLink to="/latest" isActive={isActive('/latest')}>Latest</NavLink>
          <NavLink to="/#team">Team</NavLink>
          <NavLink to="/#contact">Contact Us</NavLink>
          <Button
            variant="accent"
            size="small"
            as={Link}
            to="/donate"
          >
            Donate
          </Button>
        </Nav>

        <MobileMenuButton onClick={toggleMobileMenu}>
          ☰
        </MobileMenuButton>
      </HeaderContainer>

      <MobileMenu isOpen={mobileMenuOpen}>
        <MobileMenuCloseButton onClick={toggleMobileMenu}>✕</MobileMenuCloseButton>
        <Logo src={logo} alt="Project Assist Logo" style={{ alignSelf: 'center', margin: '1rem 0 2rem' }} />
        <MobileNavLink to="/" isActive={isActive('/')} onClick={toggleMobileMenu}>Home</MobileNavLink>
        <MobileNavLink to="/our-projects" isActive={isActive('/our-projects')} onClick={toggleMobileMenu}>Our Projects</MobileNavLink>
        <MobileNavLink to="/latest" isActive={isActive('/latest')} onClick={toggleMobileMenu}>Latest</MobileNavLink>
        <MobileNavLink to="/#team" onClick={toggleMobileMenu}>Team</MobileNavLink>
        <MobileNavLink to="/#contact" onClick={toggleMobileMenu}>Contact Us</MobileNavLink>
        <MobileDonateButtonWrapper>
          <Button
            variant="accent"
            size="medium"
            as={Link}
            to="/donate"
            fullWidth
            onClick={toggleMobileMenu}
          >
            Donate
          </Button>
        </MobileDonateButtonWrapper>
      </MobileMenu>
    </HeaderWrapper>
  );
};

export default Header; 